import { useBackendApi } from './backendApi';
import { useEventsPublisher, useEventsSubscriber } from 'event-bus';
import { useCacheService } from './cacheService';

export interface CreateEmailDomainRequest {
  domain: string;
}

export enum DomainVerificationType {
  SES_TXT = 'SES_TXT',
  SES_DKIM = 'SES_DKIM',
  SES_DMARC = 'SES_DMARC',
  SES_SEND_FROM_DOMAIN = 'SES_SEND_FROM_DOMAIN',
}

export interface DnsEntry {
  type: string,
  name: string,
  value: string,
  priority?: number,
}

export interface DomainVerificationData {
  isVerified: boolean;
  type: DomainVerificationType;
  entries: DnsEntry[];
}

export enum VerificationStatus {
  PENDING = 'pending',
  VERIFIED = 'verified',
  REJECTED = 'rejected',
}

export enum EmailDomainLevel {
  TEAM = 'team',
  ORGANIZATION = 'organization',
}

export enum TeamDomainPermission {
  MANAGE = 'MANAGE',
}

export interface EmailDomain {
  id: number;
  domain: string;
  verifications: DomainVerificationData[];
  status: VerificationStatus;
  level: EmailDomainLevel;
  _permissions: TeamDomainPermission[];
}

export const DOMAINS_UPDATED = 'DOMAINS_UPDATED';

export const TEAM_DOMAINS_CACHE_KEY = 'teamDomains';

export function useEmailDomainsService() {
  const nekstApi = useBackendApi();

  const cacheService = useCacheService('emailDomainsService');

  const eventsPublisher = useEventsPublisher();

  useEventsSubscriber('emailDomainsService', {
    [DOMAINS_UPDATED]: () => {
      cacheService.clear(TEAM_DOMAINS_CACHE_KEY);
    }
  }, []);

  const createTeamDomain = async (request: CreateEmailDomainRequest): Promise<EmailDomain> => {
    const response = await nekstApi.post('/email/domains', request) as EmailDomain;
    eventsPublisher.publish(DOMAINS_UPDATED, {});
    return response;
  };

  const getTeamDomains = async (): Promise<EmailDomain[]> => {
    return cacheService.get(TEAM_DOMAINS_CACHE_KEY, async (cacheItem) => {
      cacheItem.setExpireInSeconds(60);

      return await nekstApi.get('/email/domains') as EmailDomain[];
    });
  };

  const deleteTeamDomain = async (domainId: number): Promise<void> => {
    await nekstApi.delete(`/email/domains/${domainId}`);
    eventsPublisher.publish(DOMAINS_UPDATED, {});
  };

  const submitForReview = async (domainId: number): Promise<void> => {
    await nekstApi.post(`/email/domains/${domainId}/submitforreview`);
    eventsPublisher.publish(DOMAINS_UPDATED, {});
  };

  const verifyDomain = async (domainId: number): Promise<void> => {
    await nekstApi.post(`/email/domains/${domainId}/verify`);
    eventsPublisher.publish(DOMAINS_UPDATED, {});
  };

  return {
    createTeamDomain,
    getTeamDomains,
    deleteTeamDomain,
    submitForReview,
    verifyDomain,
  };
}
